import { z } from 'zod';
import { nullableList } from '../../zod-helpers';

const uuid = z.string().uuid();
const isoCurrency = z
  .string()
  .length(3)
  .regex(/^[A-Z]{3}$/, { message: 'Use a 3-letter ISO 4217 code (e.g. USD).' });

export const productAvailabilitySchema = z.enum(['coming_soon', 'available', 'sold_out']);
export type ProductAvailability = z.infer<typeof productAvailabilitySchema>;

export const PRODUCT_CATEGORIES = [
  'everyday',
  'occasion',
  'modest',
  'swim',
  'active',
  'accessories',
  'shoes',
  'mens',
  'kids',
] as const;
export const productCategorySchema = z.enum(PRODUCT_CATEGORIES);
export type ProductCategory = z.infer<typeof productCategorySchema>;

export const PRODUCT_PILLARS = ['style', 'food', 'health', 'family', 'culture'] as const;
export const productPillarSchema = z.enum(PRODUCT_PILLARS);
export type ProductPillar = z.infer<typeof productPillarSchema>;

const slug = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, {
    message: 'Use lower-case letters, digits, and dashes only.',
  });

const variantInline = z.object({
  id: uuid,
  size_label: z.string(),
  sku: z.string(),
  price_cents: z.number().int().nullable().optional(),
  currency: z.string().nullable().optional(),
  deleted_at: z.string().nullable().optional(),
});

const productObjectSchema = z.object({
  id: uuid,
  slug: z.string(),
  designer_id: uuid,
  designer_name: z.string().nullable().optional(),
  name_en: z.string(),
  name_ar: z.string().nullable().optional(),
  name_eg_ar: z.string().nullable().optional(),
  description_en: z.string().nullable().optional(),
  description_ar: z.string().nullable().optional(),
  description_eg_ar: z.string().nullable().optional(),
  accent_color: z.string().nullable().optional(),
  availability: productAvailabilitySchema,
  base_currency: z.string(),
  base_price_cents: z.number().int().nonnegative(),
  tags: z.array(z.string()).nullable().optional(),
  primary_media_id: uuid.nullable().optional(),
  media_ids: z.array(uuid).nullable().optional(),
  variants: z.array(variantInline).nullable().optional(),
  category: productCategorySchema.nullable().optional(),
  primary_pillar: productPillarSchema.nullable().optional(),
  size_guide_url: z.string().url().nullable().optional(),
  model_height_cm: z.number().int().positive().max(250).nullable().optional(),
  model_size_label: z.string().max(40).nullable().optional(),
  haes_aligned: z.boolean().nullable().optional(),
  created_at: z.string().nullable().optional(),
  deleted_at: z.string().nullable().optional(),
  name_missing_translation: z.boolean().nullable().optional(),
});

// The live admin API emits `currency`/`price_cents` and a boolean `deleted` flag;
// older fixtures (MSW, tests) use `base_currency`/`base_price_cents`/`deleted_at`.
// Normalize at the boundary so consumers see one shape.
export const productSchema = z.preprocess((raw) => {
  if (!raw || typeof raw !== 'object') return raw;
  const obj = raw as Record<string, unknown>;
  const next: Record<string, unknown> = { ...obj };
  if (next.base_currency === undefined && typeof next.currency === 'string') {
    next.base_currency = next.currency;
  }
  if (next.base_price_cents === undefined && typeof next.price_cents === 'number') {
    next.base_price_cents = next.price_cents;
  }
  if (next.deleted_at === undefined && typeof next.deleted === 'boolean') {
    next.deleted_at = next.deleted
      ? (typeof next.created_at === 'string' ? next.created_at : new Date(0).toISOString())
      : null;
  }
  return next;
}, productObjectSchema);
export type Product = z.infer<typeof productSchema>;
export const productListSchema = nullableList(productSchema);

export const productCreateSchema = z.object({
  slug,
  designer_id: uuid,
  name_en: z.string().min(1).max(300),
  name_ar: z.string().max(300).optional(),
  name_eg_ar: z.string().max(300).optional(),
  description_en: z.string().max(5000).optional(),
  description_ar: z.string().max(5000).optional(),
  description_eg_ar: z.string().max(5000).optional(),
  accent_color: z.string().max(20).optional(),
  availability: productAvailabilitySchema.optional(),
  base_currency: isoCurrency,
  base_price_cents: z.number().int().nonnegative(),
  tags: z.array(z.string().min(1).max(60)).max(20).optional(),
  category: productCategorySchema.optional(),
  primary_pillar: productPillarSchema.optional(),
  size_guide_url: z.string().url().max(400).optional(),
  model_height_cm: z.number().int().positive().max(250).optional(),
  model_size_label: z.string().max(40).optional(),
  haes_aligned: z.boolean().optional(),
});
export type ProductCreate = z.infer<typeof productCreateSchema>;

export const productPatchSchema = z.object({
  name_en: z.string().min(1).max(300).optional(),
  name_ar: z.string().max(300).optional(),
  name_eg_ar: z.string().max(300).optional(),
  description_en: z.string().max(5000).optional(),
  description_ar: z.string().max(5000).optional(),
  description_eg_ar: z.string().max(5000).optional(),
  accent_color: z.string().max(20).optional(),
  availability: productAvailabilitySchema.optional(),
  base_price_cents: z.number().int().nonnegative().optional(),
  tags: z.array(z.string().min(1).max(60)).max(20).optional(),
  category: productCategorySchema.optional(),
  primary_pillar: productPillarSchema.optional(),
  size_guide_url: z.string().url().max(400).optional(),
  model_height_cm: z.number().int().positive().max(250).optional(),
  model_size_label: z.string().max(40).optional(),
  haes_aligned: z.boolean().optional(),
});
export type ProductPatch = z.infer<typeof productPatchSchema>;

export const productCreateResponseSchema = z.object({ id: uuid });

export const productMediaSetSchema = z.object({
  media_ids: z.array(uuid).max(40),
});
export type ProductMediaSet = z.infer<typeof productMediaSetSchema>;

export const productListFiltersSchema = z.object({
  q: z.string().optional(),
  designer_id: uuid.optional(),
  availability: productAvailabilitySchema.optional(),
  include_deleted: z.boolean().optional(),
  limit: z.number().int().min(1).max(200).optional(),
});
export type ProductListFilters = z.infer<typeof productListFiltersSchema>;
